import type { AnimationEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
    genieCloseAnimation,
    genieOpenAnimation,
    PANEL_ANIMATION_DURATION_MS,
} from "app/pages/sales-intelligence/common-components/animations";

// Buffer past the CSS duration so the fallback timer only fires if the animationend event never arrives.
const FALLBACK_BUFFER_MS = 80;

type OverlayPhase = "closed" | "opening" | "open" | "closing";

type HookParams = {
    isExpanded: boolean;
};

type HookValue = {
    shouldRenderOverlay: boolean;
    isOpen: boolean;
    isFullyOpen: boolean;
    skipEntryAnimation: boolean;
    onAnimationEnd: (event: AnimationEvent<HTMLElement>) => void;
};

// True only when the genie open/close animation on the overlay container itself finishes (not a
// bubbled child animation, and not a stale event from a superseded animation). styled-components
// emits a hashed keyframe name, so we must compare against the live `.getName()`.
const isGenieAnimationEnd = (event: AnimationEvent<HTMLElement>, isOpen: boolean): boolean =>
    event.target === event.currentTarget &&
    event.animationName === (isOpen ? genieOpenAnimation : genieCloseAnimation).getName();

/**
 * Owns the overlay's open/close lifecycle as an explicit phase machine. The entry/exit genie
 * completion is driven by `animationend` (accurate, no magic-number coupling) with a fallback timer
 * so progress is guaranteed even when the event never fires (reduced-motion, interrupted animation,
 * backgrounded tab). Phase guards make completion idempotent, so rapid open↔close cannot unmount
 * mid-animation or hang.
 */
export const useOverlayAnimationGate = ({ isExpanded }: HookParams): HookValue => {
    const [phase, setPhase] = useState<OverlayPhase>(isExpanded ? "open" : "closed");

    // An overlay that mounts already-expanded (e.g. route-driven) has no entry animation.
    const skipEntryAnimationRef = useRef(isExpanded);
    const prevExpandedRef = useRef(isExpanded);

    useEffect(() => {
        if (isExpanded === prevExpandedRef.current) {
            return;
        }

        prevExpandedRef.current = isExpanded;

        if (isExpanded) {
            skipEntryAnimationRef.current = false;
            setPhase("opening");
        } else {
            setPhase((current) => (current === "closed" ? "closed" : "closing"));
        }
    }, [isExpanded]);

    const complete = useCallback(() => {
        setPhase((current) => {
            if (current === "opening") {
                return "open";
            }

            if (current === "closing") {
                return "closed";
            }

            return current;
        });
    }, []);

    useEffect(() => {
        if (phase !== "opening" && phase !== "closing") {
            return;
        }

        const timeoutId = setTimeout(complete, PANEL_ANIMATION_DURATION_MS + FALLBACK_BUFFER_MS);

        return () => clearTimeout(timeoutId);
    }, [phase, complete]);

    const onAnimationEnd = useCallback(
        (event: AnimationEvent<HTMLElement>) => {
            if (isGenieAnimationEnd(event, isExpanded)) {
                complete();
            }
        },
        [isExpanded, complete],
    );

    return {
        shouldRenderOverlay: phase !== "closed",
        isOpen: isExpanded,
        isFullyOpen: phase === "open",
        skipEntryAnimation: skipEntryAnimationRef.current,
        onAnimationEnd,
    };
};
